import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  listClasses,
  createClass,
  listSubjects,
  listStaff,
  TEACHER_DEPARTMENT,
  listSubjectCombinations,
} from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

const DEFAULT_COL_WIDTH = 120;
const MIN_COL_WIDTH = 70;

// Thứ tự cột môn học cố định: Địa lý đứng ngay sau Lịch sử, Tiếng Anh đẩy
// xuống cột cuối cùng. Môn không có trong danh sách giữ nguyên thứ tự gốc,
// xếp trước các môn đã định vị trí ở cuối.
const SUBJECT_DISPLAY_ORDER = [
  "ngu van",
  "toan",
  "sinh hoc",
  "lich su",
  "dia ly",
  "cong nghe",
  "gdkt&pl",
  "vat ly",
  "hoa hoc",
  "tieng anh",
];
function normalizeSubjectName(name) {
  return (name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .trim();
}
function sortSubjectsByDisplayOrder(subjs) {
  const rank = (s) => {
    const idx = SUBJECT_DISPLAY_ORDER.indexOf(normalizeSubjectName(s.name));
    return idx === -1 ? SUBJECT_DISPLAY_ORDER.length : idx;
  };
  return subjs
    .map((s, originalIndex) => ({ s, originalIndex }))
    .sort((a, b) => rank(a.s) - rank(b.s) || a.originalIndex - b.originalIndex)
    .map((x) => x.s);
}

// Thanh kéo giãn cột kiểu Excel — kéo ở viền phải tiêu đề cột môn học.
function ResizeHandle({ width, onChange }) {
  const dragRef = useRef(null);

  const onMouseDown = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: width };
    const onMouseMove = (ev) => {
      if (!dragRef.current) return;
      const next = Math.max(MIN_COL_WIDTH, dragRef.current.startWidth + (ev.clientX - dragRef.current.startX));
      onChange(next);
    };
    const onMouseUp = () => {
      dragRef.current = null;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  return (
    <div
      onMouseDown={onMouseDown}
      title="Kéo để đổi độ rộng cột"
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: 6,
        cursor: "col-resize",
        userSelect: "none",
      }}
    />
  );
}

// Chỉ xem — sửa phân công GV phải làm ở trang "Soạn thảo PCCM (nháp)" rồi
// bấm "Duyệt" (để PCCM cũ được tự lưu vào lịch sử trước khi ghi đè).
function MatrixCell({ subject, cfg, teachers, width, disabled }) {
  if (disabled) {
    return (
      <td style={{ ...cellTdStyle, background: "#f3f4f6" }}>
        <div style={{ fontSize: 10, color: "#9ca3af" }}>Lớp đã giải tán</div>
      </td>
    );
  }

  const teacherName = cfg?.assignedTeacherId
    ? (teachers.find((t) => t.id === cfg.assignedTeacherId) || {}).name || cfg.assignedTeacherId
    : "";

  return (
    <td style={{ ...cellTdStyle, width, maxWidth: width }}>
      {teacherName ? (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={teacherName}>
            {teacherName}
          </div>
          <div style={{ fontSize: 10, color: "#6b7280" }}>{cfg.periodsPerWeek} tiết/tuần</div>
        </>
      ) : (
        <div style={{ fontSize: 11, color: "#d1d5db" }}>—</div>
      )}
    </td>
  );
}

const HappySchoolClasses = () => {
  const schoolId = getActiveSchoolId();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [combos, setCombos] = useState([]);
  const [name, setName] = useState("");
  const [khoi, setKhoi] = useState("12");
  const [colWidths, setColWidths] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("matrix"); // "matrix" | "teacher"

  const reload = () => {
    if (!schoolId) return;
    setLoading(true);
    Promise.all([
      listClasses(schoolId),
      listSubjects(schoolId),
      listStaff(schoolId, { department: TEACHER_DEPARTMENT }),
      listSubjectCombinations(schoolId),
    ])
      .then(([c, s, t, combos]) => {
        setClasses(c);
        setSubjects(sortSubjectsByDisplayOrder(s));
        setTeachers(t.slice().sort((a, b) => a.name.localeCompare(b.name)));
        setCombos(combos);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [schoolId]);

  // Nhóm theo GV — giống bố cục sheet "PCCM" và tab "Theo Giáo viên" ở "Soạn
  // thảo PCCM (nháp)", nhưng đọc thẳng PCCM THẬT (classes[].subjects, đã có
  // sẵn periodsPerWeek) thay vì bản nháp + bảng "Số tiết".
  const teacherReport = useMemo(() => {
    const byTeacher = {};
    classes.forEach((cls) => {
      if (cls.status === "giai_tan") return;
      const khoi = (String(cls.name).match(/^(\d{1,2})A/) || [])[1] || "?";
      (cls.subjects || []).forEach((cs) => {
        if (!cs.assignedTeacherId) return;
        const t = teachers.find((x) => x.id === cs.assignedTeacherId);
        if (!t) return;
        const subj = subjects.find((s) => s.id === cs.subjectId);
        const rec = (byTeacher[cs.assignedTeacherId] = byTeacher[cs.assignedTeacherId] || {
          name: t.name,
          employmentType: t.employmentType,
          dobYear: (String(t.dob || "").match(/^(\d{4})-/) || [])[1] || "",
          subjectNames: new Set(),
          subjectOrder: t.subjectIds || [],
          khoi: {},
          tietKhoi: {},
        });
        rec.subjectNames.add(subj ? subj.name : cs.subjectId);
        rec.khoi[khoi] = rec.khoi[khoi] || [];
        if (!rec.khoi[khoi].includes(cls.name)) rec.khoi[khoi].push(cls.name);
        rec.tietKhoi[khoi] = (rec.tietKhoi[khoi] || 0) + (Number(cs.periodsPerWeek) || 0);
      });
    });
    classes.forEach((cls) => {
      if (!cls.homeroomTeacherId) return;
      const rec = byTeacher[cls.homeroomTeacherId];
      if (!rec) return;
      const khoi = (String(cls.name).match(/^(\d{1,2})A/) || [])[1] || "?";
      rec.chuNhiem = rec.chuNhiem || {};
      rec.chuNhiem[khoi] = rec.chuNhiem[khoi] || [];
      if (!rec.chuNhiem[khoi].includes(cls.name)) rec.chuNhiem[khoi].push(cls.name);
    });
    const subjectById = Object.fromEntries(subjects.map((s) => [s.id, s]));
    const clsSortKey = (n) => { const m = String(n).match(/A(\d+)/); return m ? Number(m[1]) : 99; };
    return Object.values(byTeacher)
      .map((r) => {
        Object.keys(r.khoi).forEach((k) => r.khoi[k].sort((a, b) => clsSortKey(a) - clsSortKey(b)));
        const tongLop = Object.values(r.khoi).reduce((s, arr) => s + arr.length, 0);
        const tongTiet = Object.values(r.tietKhoi).reduce((s, n) => s + n, 0);
        const ordered = r.subjectOrder.map((sid) => (subjectById[sid] || {}).name).filter((n) => n && r.subjectNames.has(n));
        [...r.subjectNames].forEach((n) => { if (!ordered.includes(n)) ordered.push(n); });
        return { ...r, subjectNames: ordered.join(", "), tongLop, tongTiet };
      })
      .sort((a, b) => a.subjectNames.localeCompare(b.subjectNames, "vi") || a.name.localeCompare(b.name, "vi"));
  }, [classes, subjects, teachers]);

  if (!schoolId) {
    return (
      <div style={{ padding: 16 }}>
        Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link> trước.
      </div>
    );
  }

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await createClass(schoolId, name.trim());
      setName("");
      reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredClasses = classes.filter((c) => !khoi.trim() || c.name.startsWith(khoi.trim()));
  const comboById = Object.fromEntries(combos.map((co) => [co.id, co]));

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Ma trận Lớp × Môn — <b>chỉ xem</b>. Muốn sửa GV/số tiết, vào{" "}
        <Link to="/happyschool/pccm-draft" style={{ color: "#0ea5a3", fontWeight: 600 }}>📝 Soạn thảo PCCM (nháp)</Link>{" "}
        rồi bấm "Duyệt" để ghi vào đây.
      </p>

      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <button onClick={() => setTab("matrix")} style={tab === "matrix" ? tabActive : tabInactive}>Ma trận Lớp × Môn</button>
        <button onClick={() => setTab("teacher")} style={tab === "teacher" ? tabActive : tabInactive}>Theo Giáo viên</button>
      </div>

      {tab === "matrix" && (
      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <label style={{ fontSize: 13 }}>
          Khối:{" "}
          <input value={khoi} onChange={(e) => setKhoi(e.target.value)} placeholder="vd: 12" style={{ width: 60, padding: 6 }} />
        </label>

        <form onSubmit={handleCreate} style={{ display: "flex", gap: 8 }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Thêm lớp (vd: 12A14)"
            style={{ padding: 6, border: "1px solid #d1d5db", borderRadius: 6 }}
          />
          <button type="submit" style={btnStyle}>
            Thêm lớp
          </button>
        </form>
      </div>
      )}

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {loading ? (
        <p>Đang tải...</p>
      ) : tab === "teacher" ? (
        <div style={{ overflow: "auto", maxHeight: "78vh", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 13, background: "#fff", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...th, ...thFix(TCOL.stt), ...clip, top: 0, zIndex: 5, width: TCOL.wStt }}>STT</th>
                <th style={{ ...th, ...thFix(TCOL.bomon), ...clip, top: 0, zIndex: 5, textAlign: "left", width: TCOL.wBomon }}>Bộ môn</th>
                <th style={{ ...th, ...thFix(TCOL.ten), ...clip, top: 0, zIndex: 5, textAlign: "left", width: TCOL.wTen }}>Họ và tên GV</th>
                <th style={{ ...th, ...thFix(TCOL.namsinh), ...clip, top: 0, zIndex: 5, width: TCOL.wNamsinh }}>Năm sinh</th>
                <th style={{ ...th, ...thFix(TCOL.loai), ...clip, top: 0, zIndex: 5, width: TCOL.wLoai }}>Loại</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, textAlign: "left", minWidth: 200 }}>Khối 12</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, background: "#ecfdf5" }}>Số tiết K12</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, textAlign: "left", minWidth: 200 }}>Khối 11</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, background: "#ecfdf5" }}>Số tiết K11</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, textAlign: "left", minWidth: 200 }}>Khối 10</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, background: "#ecfdf5" }}>Số tiết K10</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3 }}>Tổng số lớp</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, background: "#ecfdf5" }}>Tổng số tiết</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3 }}>Chủ nhiệm lớp 12</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3 }}>Chủ nhiệm lớp 11</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3 }}>Chủ nhiệm 10</th>
              </tr>
            </thead>
            <tbody>
              {teacherReport.map((r, i) => (
                <tr key={r.name + i}>
                  <td style={{ ...td, ...tdFix(TCOL.stt), ...clip, width: TCOL.wStt }}>{i + 1}</td>
                  <td style={{ ...td, ...tdFix(TCOL.bomon), ...clip, width: TCOL.wBomon, textAlign: "left" }} title={r.subjectNames}>{r.subjectNames}</td>
                  <td style={{ ...td, ...tdFix(TCOL.ten), ...clip, width: TCOL.wTen, textAlign: "left", fontWeight: 700 }} title={r.name}>{r.name}</td>
                  <td style={{ ...td, ...tdFix(TCOL.namsinh), ...clip, width: TCOL.wNamsinh }}>{r.dobYear}</td>
                  <td style={{ ...td, ...tdFix(TCOL.loai), ...clip, width: TCOL.wLoai }} title={r.employmentType || ""}>{r.employmentType || ""}</td>
                  <td style={{ ...td, textAlign: "left" }}>{(r.khoi["12"] || []).join(" + ")}</td>
                  <td style={{ ...td, background: "#ecfdf5", fontWeight: 700 }}>{r.tietKhoi["12"] || ""}</td>
                  <td style={{ ...td, textAlign: "left" }}>{(r.khoi["11"] || []).join(" + ")}</td>
                  <td style={{ ...td, background: "#ecfdf5", fontWeight: 700 }}>{r.tietKhoi["11"] || ""}</td>
                  <td style={{ ...td, textAlign: "left" }}>{(r.khoi["10"] || []).join(" + ")}</td>
                  <td style={{ ...td, background: "#ecfdf5", fontWeight: 700 }}>{r.tietKhoi["10"] || ""}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{r.tongLop}</td>
                  <td style={{ ...td, background: "#ecfdf5", fontWeight: 700 }}>{r.tongTiet}</td>
                  <td style={td}>{((r.chuNhiem || {})["12"] || []).join(" + ")}</td>
                  <td style={td}>{((r.chuNhiem || {})["11"] || []).join(" + ")}</td>
                  <td style={td}>{((r.chuNhiem || {})["10"] || []).join(" + ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : subjects.length === 0 ? (
        <p style={{ color: "#6b7280" }}>Chưa có môn học nào — tạo môn học ở trang "Môn học" trước.</p>
      ) : filteredClasses.length === 0 ? (
        <p style={{ color: "#6b7280" }}>Không có lớp nào khớp Khối "{khoi}".</p>
      ) : (
        <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "70vh" }}>
          <table style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={cornerThStyle}>Lớp</th>
                {subjects.map((subj) => {
                  const width = colWidths[subj.id] || DEFAULT_COL_WIDTH;
                  return (
                    <th key={subj.id} style={{ ...theadThStyle, width, maxWidth: width }}>
                      {subj.name}
                      <ResizeHandle width={width} onChange={(w) => setColWidths((cw) => ({ ...cw, [subj.id]: w }))} />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredClasses.map((c) => {
                const cfgBySubject = Object.fromEntries((c.subjects || []).map((cs) => [cs.subjectId, cs]));
                const disabled = c.status === "giai_tan";
                return (
                  <tr key={c.id} style={disabled ? { opacity: 0.6 } : undefined}>
                    <td style={classNameCellStyle}>
                      <strong>{c.name}</strong>
                      {disabled && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#dc2626" }}>GIẢI TÁN</span>}
                      {c.combinationId && comboById[c.combinationId] && (
                        <div style={{ fontSize: 10, color: "#6366f1", fontWeight: 600 }}>{comboById[c.combinationId].name}</div>
                      )}
                    </td>
                    {subjects.map((subj) => (
                      <MatrixCell
                        key={subj.id}
                        subject={subj}
                        cfg={cfgBySubject[subj.id]}
                        teachers={teachers}
                        width={colWidths[subj.id] || DEFAULT_COL_WIDTH}
                        disabled={disabled}
                      />
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const btnStyle = { padding: "6px 12px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 };
const thStyle = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", fontSize: 13, whiteSpace: "nowrap" };
// Đóng băng kiểu Excel: hàng tiêu đề (Bộ môn) dính trên khi cuộn dọc, cột Lớp dính trái khi cuộn ngang.
const theadThStyle = { ...thStyle, position: "sticky", top: 0, zIndex: 2 };
const classNameCellStyle = { ...thStyle, textAlign: "left", position: "sticky", left: 0, zIndex: 1 };
// Ô góc (giao giữa hàng tiêu đề + cột Lớp) phải dính cả 2 chiều, z-index cao nhất.
const cornerThStyle = { ...thStyle, position: "sticky", top: 0, left: 0, zIndex: 3 };
const cellTdStyle = { border: "1px solid #e5e7eb", padding: 4, textAlign: "center" };

// --- Tab "Theo Giáo viên" — style giống hệt "Soạn thảo PCCM (nháp)" ---
const tabInactive = { padding: "7px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const tabActive = { ...tabInactive, background: "#0ea5a3", color: "#fff", border: "1px solid #0ea5a3" };
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap" };
const thFix = (left) => ({ position: "sticky", left, zIndex: 4, background: "#f3f4f6" });
const td = { border: "1px solid #e5e7eb", padding: "4px 6px", textAlign: "center" };
const tdFix = (left) => ({ position: "sticky", left, zIndex: 1, background: "#fff" });
const clip = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", boxSizing: "border-box" };
const TCOL_W = { stt: 44, bomon: 130, ten: 180, namsinh: 80, loai: 100 };
const TCOL = {
  wStt: TCOL_W.stt, wBomon: TCOL_W.bomon, wTen: TCOL_W.ten, wNamsinh: TCOL_W.namsinh, wLoai: TCOL_W.loai,
  stt: 0,
  bomon: TCOL_W.stt,
  ten: TCOL_W.stt + TCOL_W.bomon,
  namsinh: TCOL_W.stt + TCOL_W.bomon + TCOL_W.ten,
  loai: TCOL_W.stt + TCOL_W.bomon + TCOL_W.ten + TCOL_W.namsinh,
};

export default HappySchoolClasses;
